import { useCallback, useState } from "react"

import { amendEvacPostBodyFormData } from "./postBodyGenerators"
import { amendAcuPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/acuSpec"
import { amendEbPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/ebSpec"
import { amendEvdcPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { amendHpPostBodyFromData } from "../../../../../components/devices/lct/formSpecs/hpSpec"
import {
    EVACDeviceFormData,
    EVDCDeviceFormData,
    HpDeviceFormData,
    isLctFormDataAcu,
    isLctFormDataEb,
    isLctFormDataEvac,
    isLctFormDataEvdc,
    isLctFormDataHp,
} from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import {
    internalAppError,
    invalidApiDataError,
} from "../../../../../errors/recognisedErrors"
import {
    getTsIoMapper,
    snackbar,
} from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    PublishLCTAmendmentResponse,
    TPublishLCTAmendmentResponse,
} from "../../../types/devices/lct/publishLctGeneralTypes"
import { PUBLISH_LCT_AMENDMENT } from "../../../urls/private"

const amendLCTDeviceMapper = getTsIoMapper(
    PublishLCTAmendmentResponse,
    "amendLCTDeviceMapper"
)

interface IPublishLCTDeviceAmendmentOutput {
    publishLCTDeviceAmendment: () => Promise<TPublishLCTAmendmentResponse>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

const usePublishLCTDeviceAmendment = (
    formData: EVACDeviceFormData | EVDCDeviceFormData | HpDeviceFormData,
    dataset: LCTOnlyDatasets,
    systemReference: string,
    attachmentUUID?: string
): IPublishLCTDeviceAmendmentOutput => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const publishLCTDeviceAmendment =
        useCallback(async (): Promise<TPublishLCTAmendmentResponse> => {
            let response: TPublishLCTAmendmentResponse = {
                published_device_id: "",
                amendment_ref: "",
            }
            if (!attachmentUUID || !systemReference) {
                return response
            }

            let postBody = {}
            if (isLctFormDataEvac(formData, dataset)) {
                postBody = amendEvacPostBodyFormData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else if (isLctFormDataHp(formData, dataset)) {
                postBody = amendHpPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else if (isLctFormDataEb(formData, dataset)) {
                postBody = amendEbPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else if (isLctFormDataAcu(formData, dataset)) {
                postBody = amendAcuPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else if (isLctFormDataEvdc(formData, dataset)) {
                postBody = amendEvdcPostBodyFromData(
                    formData,
                    attachmentUUID,
                    systemReference
                )
            } else {
                const appError = internalAppError(
                    "Invalid dataset provided to publishLCTDeviceAmendment"
                )
                setError(appError)
                snackbar(
                    `Couldn't amend device, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
                return response
            }

            try {
                setIsLoading(true)
                const postResponse = await postClientProtected(
                    PUBLISH_LCT_AMENDMENT,
                    postBody
                )
                response = amendLCTDeviceMapper.map(postResponse)
                snackbar("Successfully amended device", "success")
                setIsLoaded(true)
            } catch {
                const appError = invalidApiDataError("Couldn't amend Device")
                setError(appError)
                snackbar(
                    `Couldn't amend device, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
            } finally {
                setIsLoading(false)
            }
            return response
        }, [attachmentUUID, systemReference, formData, dataset])

    return {
        publishLCTDeviceAmendment,
        isLoading,
        isLoaded,
        error,
    }
}

export default usePublishLCTDeviceAmendment
